import { ShipGrowth } from '@prometheus/types'

// Population growth is banded: the ship grows by `growth` per year while the
// crew is under `count` (see getPopulationGrowth, which walks the same
// ascending list). Crossing a band boundary is a permanent raise to the growth
// rate, so a player sitting just under one wants to buy the gap instead of
// waiting for it. This works out what that purchase is.

export interface PopulationTierTarget {
  /** Crew size that switches the ship into the next growth band. */
  threshold: number
  /** People to hire right now to reach it. */
  needed: number
  /** Growth per year the ship gets today. */
  currentGrowth: number
  /** Growth per year once the threshold is reached. */
  nextGrowth: number
  /** Cash the hire costs at the current population price. */
  cost: number
  affordable: boolean
  /** False when the threshold is above the ship's current maximum capacity. */
  withinCapacity: boolean
}

export interface PopulationTierInput {
  count: number
  ship_growth: ShipGrowth[]
  max_population: number
  population_cost: number
  cash: number
}

export function getNextGrowthTier({
  count,
  ship_growth,
  max_population,
  population_cost,
  cash,
}: PopulationTierInput): PopulationTierTarget | null {
  if (!ship_growth || !ship_growth.length) return null

  const population = Number.isFinite(count) ? Math.max(0, Math.floor(count)) : 0
  const index = ship_growth.findIndex(band => population < band.count)
  // Past the top band (or a malformed list) — nothing left to unlock.
  if (index === -1) return null

  const current = ship_growth[index]
  const next = ship_growth[index + 1]
  if (!next || next.growth <= current.growth) return null

  const needed = current.count - population
  if (needed <= 0) return null

  const cost = needed * (population_cost || 0)

  return {
    threshold: current.count,
    needed,
    currentGrowth: current.growth,
    nextGrowth: next.growth,
    cost,
    affordable: cost <= cash,
    withinCapacity: current.count <= max_population,
  }
}
